import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Scrollbar from 'smooth-scrollbar';
import './Content.css';


// URL zur content.json
const CONTENT_URL = `assets/data/content.json?t=${new Date().getTime()}`;

const Content = ({ isVisible, hotspotName, onClose }) => {
  const [currentContent, setCurrentContent] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayContent, setDisplayContent] = useState(null);
  const [contentData, setContentData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScrollHint, setShowScrollHint] = useState(true);
  const timeoutRef = useRef(null);
  const scrollableContentRef = useRef(null);

  const scrollRef = useRef(null);


  // Check if there are pages to display and more than one page
  const hasMultiplePages = useMemo(() => {
    return displayContent?.pages && displayContent.pages.length > 1;
  }, [displayContent]);


  useEffect(() => {
    if (scrollRef.current && displayContent) {
      const scrollbar = Scrollbar.init(scrollRef.current, {
        damping: 0.05,
        thumbMinSize: 10,
      });

      return () => {
        scrollbar?.destroy();
      };
    }
  }, [displayContent]);

  // Show/hide scroll hint logic
  useEffect(() => {
    // Reset and show the hint when content changes and has multiple pages
    if (displayContent?.pages?.length > 1) {
      setShowScrollHint(true);

      // Hide the hint after 4 seconds
      const timer = setTimeout(() => {
        setShowScrollHint(false);
      }, 4000);

      return () => clearTimeout(timer);
    } else {
      setShowScrollHint(false);
    }
  }, [displayContent]); // Run when displayContent changes

  // Lade die content.json beim ersten Rendern
  useEffect(() => {
    const fetchContentData = async () => {
      try {
        const response = await fetch(CONTENT_URL, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setContentData(data);
        setIsLoading(false);
      } catch (err) {
        console.error('Fehler beim Laden der Inhalte:', err);
        setError('Inhalte konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
        setIsLoading(false);
      }
    };

    fetchContentData();

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Scroll to top when displayContent changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, [displayContent]);

  // Behandle Änderungen am ausgewählten Hotspot
  useEffect(() => {
    if (!hotspotName || isLoading) return;

    // Starte Übergangseffekt
    setIsTransitioning(true);

    // Nach dem Ausblenden den Inhalt aktualisieren und wieder einblenden
    timeoutRef.current = setTimeout(() => {
      const content = contentData.find(item => item.object_name === hotspotName);
      if (content) {
        setCurrentContent(content);
        setDisplayContent(content);
      } else {
        setCurrentContent(null);
        setDisplayContent(null);
        console.warn(`Kein Inhalt für Hotspot '${hotspotName}' gefunden`);
      }
      setIsTransitioning(false);
    }, 300); // Sollte mit der CSS-Übergangszeit übereinstimmen

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [hotspotName, contentData, isLoading]);

  // Ladeanzeige anzeigen, während die Daten geladen werden
  if (isLoading) {
    return (
      <div className={`content-container ${isVisible ? 'active' : ''}`}>
        <div className="loading-message">Lade Inhalte...</div>
      </div>
    );
  }

  // Fehlermeldung anzeigen, falls ein Fehler aufgetreten ist
  if (error) {
    return (
      <div className={`content-container ${isVisible ? 'active' : ''}`}>
        <div className="error-message">{error}</div>
      </div>
    );
  }


  return (
    <div className={`content-container ${isVisible ? 'active' : ''}`}>
      <button className="close-button" onClick={onClose}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
      {displayContent && (
        <div className={`content ${isTransitioning ? 'fade-out' : isVisible ? 'fade-in' : 'fade-out'}`}>
          <div className="scrollable-content" ref={scrollRef}>
            <div className="content-inner">
              <div className="content-header">
                <h2 dangerouslySetInnerHTML={{ __html: displayContent.title }} />
                <p>{displayContent.company}</p>
              </div>
              {displayContent && displayContent.content && displayContent.content.map((item, index) => {
                switch (item.type) {
                  case 'header-image':
                    return (
                      <div key={index} className="content-block header-image">
                        <img src={item.image} alt={item.text} />
                        {item.text && <div className="image-caption">{item.text}</div>}
                      </div>
                    );
                  case 'headline':
                    return (
                      <div key={index} className="content-block headline">
                        <h3>{item.text}</h3>
                      </div>
                    );
                  case 'text':
                    return (
                      <div key={index} className="content-block two-columns">
                        <div className="column left">
                          {item['text-left'] && <p>{item['text-left']}</p>}
                        </div>
                        <div className="column right">
                          {item['text-right'] && <p className="text-right">{item['text-right']}</p>}
                        </div>
                      </div>
                    );
                  case 'text-button':
                    return (
                      <div key={index} className="content-block two-columns">
                        <div className="column left">
                          {item['text-left'] && <p>{item['text-left']}</p>}
                        </div>
                        <div className="column right">
                          {item['text-right'] && <p className="text-right" dangerouslySetInnerHTML={{ __html: item['text-right'] }}></p>}
                          {item['button'] && <a href={item['link']} target="_blank" className="button" rel="noopener noreferrer">{item['button']}</a>}
                        </div>
                      </div>
                    );
                  case 'image':
                    return (
                      <div key={index} className="content-block image">
                        <img src={item.image} alt={item.text} />
                        {item.text && <div className="image-caption">{item.text}</div>}
                      </div>
                    );
                  default:
                    return null;
                }
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Content;
